<template>
  <div class="teilnehmer-person-list">
    <div
      v-for="(teilnehmer, index) in teilnehmerListe"
      :key="teilnehmer.nummer != null ? teilnehmer.nummer : index"
      class="teilnehmer-person-list__entry"
      :class="{ 'teilnehmer-person-list__entry--active': index === selectedIndex }"
      @click="$emit('select', index)"
    >
      <div class="d-flex align-items-center">
        <!-- Der Name wird immer als reiner Text angezeigt; beim ersten Teilnehmer (Hauptkunde)
             erscheint rechts daneben ein Info-Icon, das die Kunden-Detailinfos als Popover zeigt -->
        <div class="teilnehmer-person-list__name text-truncate" style="min-width: 0">
          <span>{{ getAnredeAusGeschlecht(teilnehmer.geschlecht) }} {{ formatName(teilnehmer.name) }}</span>
        </div>

        <kunden-name-popover
          v-if="index === 0"
          triggerIcon
          class="ml-2 flex-shrink-0"
          :kunde="kunde?.master || teilnehmer.name"
          :teilnehmende="[teilnehmer]"
          :agentur="agentur"
        ></kunden-name-popover>
        <span
          v-if="verlaufEnabled"
          class="teilnehmer-person-list__verlauf ml-auto flex-shrink-0"
          title="Verlauf anzeigen"
          v-b-tooltip.hover
          @click.stop="$emit('show-verlauf', teilnehmer)"
        >
          <History :size="15" />
        </span>
      </div>
      <div class="d-flex text-muted font-size-sm">
        <span>{{ teilnehmer.geburtsdatum | date }}</span>
        <span v-if="berechneAlter(teilnehmer.geburtsdatum) != null" class="ml-2">
          ({{ berechneAlter(teilnehmer.geburtsdatum) }})
        </span>
        <span
          v-if="hatGeburtstagWaehrendReise(teilnehmer)"
          v-b-tooltip.hover
          title="Geburtstag während der Reise"
          class="ml-1 d-inline-flex align-items-center"
        >
          <Cake :size="15" style="color: #004a99" />
        </span>
      </div>

      <!-- Fortschrittsanzeige entfällt vollständig, wenn für den Teilnehmer keine Daten benötigt werden;
           einzelne Kategorien werden nur angezeigt, wenn sie laut Konfiguration erforderlich sind -->
      <template v-if="progressList[index].total > 0">
        <div class="teilnehmer-person-list__progress-bar mt-3 mb-2">
          <div
            class="teilnehmer-person-list__progress-fill"
            :class="
              progressList[index].isComplete
                ? 'teilnehmer-person-list__progress-fill--complete'
                : 'teilnehmer-person-list__progress-fill--partial'
            "
            :style="{ width: progressList[index].percent + '%' }"
          ></div>
        </div>
        <div class="d-flex text-muted font-size-sm" style="gap: 24px">
          <span v-if="progressList[index].passdaten.total > 0">
            Passdaten: {{ progressList[index].passdaten.filled }}/{{ progressList[index].passdaten.total }}
          </span>
          <span v-if="progressList[index].dokumente.total > 0">
            Dokumente: {{ progressList[index].dokumente.filled }}/{{ progressList[index].dokumente.total }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import KundenNamePopover from '@/components/kunden/kunden-name-popover.vue';
import {
  getPassdatenProgress,
  getDokumenteProgress,
  isBirthdayDuringTravelPeriod,
  berechneAlter,
  getAnredeAusGeschlecht,
} from '@/core/vorgaenge/helpers/teilnehmer-passdaten.utils';
import { History, Cake } from 'lucide-vue';
import { formatName } from '@/core/common/helpers/utils';

export default {
  name: 'TeilnehmerPersonList',
  components: { KundenNamePopover, History, Cake },
  props: {
    teilnehmerListe: { type: Array, default: () => [] },
    selectedIndex: { type: Number, default: 0 },
    kunde: { type: Object },
    agentur: { type: Object },
    konfiguration: { type: Object },
    reiseterminStartdatum: { type: [String, Object] },
    reiseterminEnddatum: { type: [String, Object] },
    dokumenteByTeilnehmer: { type: Object, default: () => ({}) },
    verlaufEnabled: { type: Boolean, default: true },
  },
  computed: {
    progressList() {
      return this.teilnehmerListe.map(teilnehmer => {
        const passdaten = getPassdatenProgress(teilnehmer, this.konfiguration);
        const dokumente = getDokumenteProgress(
          this.dokumenteByTeilnehmer[teilnehmer.nummer] || [],
          this.konfiguration
        );
        const total = passdaten.total + dokumente.total;
        const filled = passdaten.filled + dokumente.filled;
        return {
          passdaten,
          dokumente,
          total,
          percent: total > 0 ? Math.round((filled / total) * 100) : 0,
          isComplete: total > 0 && filled === total,
        };
      });
    },
  },
  methods: {
    formatName,
    berechneAlter,
    getAnredeAusGeschlecht,
    hatGeburtstagWaehrendReise(teilnehmer) {
      return isBirthdayDuringTravelPeriod(
        teilnehmer.geburtsdatum,
        this.reiseterminStartdatum,
        this.reiseterminEnddatum
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.teilnehmer-person-list {
  padding-top: 12px;
}
.teilnehmer-person-list__entry {
  padding: 16px;
  border-bottom: 1px solid #ebedf3;
  border-left: 3px solid transparent;
  cursor: pointer;
  background: #fff;

  &:first-child {
    border-top: 1px solid #ebedf3;
  }

  &:hover {
    background-color: #f9fafb;
  }

  &--active {
    border-left-color: #3699ff;
    background-color: #f1f9ff;

    &:hover {
      background-color: #f1f9ff;
    }
  }
}
.teilnehmer-person-list__name {
  color: #3699ff;
  font-weight: 600;
  font-size: 1.05rem;

  :deep(b) {
    color: #3699ff;
  }
}
.teilnehmer-person-list__verlauf {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #3699ff;
  cursor: pointer;
  line-height: 1;

  &:hover {
    border-color: #3699ff;
    color: #3699ff;
  }
}
.teilnehmer-person-list__progress-bar {
  height: 6px;
  border-radius: 3px;
  background-color: #ebedf3;
  overflow: hidden;
}
.teilnehmer-person-list__progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;

  &--complete {
    background: linear-gradient(90deg, #c9f7f5 0%, #1bc5bd 100%);
  }

  &--partial {
    background: linear-gradient(90deg, #ffe2a8 0%, #ffa800 100%);
  }
}
</style>
